import React from "react";
import { Grid } from "Components/Base/Layout/Grid";
import { Typography } from "Components/Base/DataDisplay/Typography";
import useMobileViewportCheck from "../../../Portal/web/Content/js/lib/effects/useMobileViewportCheck";
import { renderTitleLinkContent } from "./titleLinkContent";
import { STYLE_DESKTOP_LIST_ITEM, STYLE_MOBILE_LIST_ITEM } from "./style";
import "./style.css";

/**
 * A list item component for displaying learning activities
 * @param {string} props.id - Unique identifier for the activity
 * @param {string} props.title - The title of the activity
 * @param {string} props.type - The type of activity
 * @param {React.ReactNode} props.chips - Chip components to display
 * @param {React.ReactNode} props.content - The main content of the activity
 * @param {React.ReactNode} props.actions - Action components to display
 * @param {boolean} [props.displayImage=false] - Whether to display the image component
 * @param {React.ReactNode} [props.imageComponent] - Image component to display
 * @param {{ href: string, linkTitle?: string }} [props.titleLink] - When set, the title is a link to this URL
 * @returns {React.ReactElement} The learning activity list item component
 */
const LearningActivityListItem = (props) => {
  const mobileViewport = useMobileViewportCheck(
    window.matchMedia("(max-width: 900px)"),
  );

  return mobileViewport ? (
    <LearningActivityMobileListItem {...props} />
  ) : (
    <LearningActivityDesktopListItem {...props} />
  );
};

const LearningActivityDesktopListItem = ({
  title,
  type,
  chips,
  content,
  actions,
  displayImage = false,
  imageComponent,
  titleLink,
}) => {
  return (
    <Grid
      container
      wrap="nowrap"
      alignItems="flex-start"
      className="learning-courselist-item"
      sx={STYLE_DESKTOP_LIST_ITEM}
    >
      {displayImage && (
        <Grid
          item
          xs="auto"
          className="learning-courselist-item-image"
          sx={{ flexShrink: 0 }}
        >
          {imageComponent}
        </Grid>
      )}
      <Grid
        container
        item
        xs
        direction="column"
        className="learning-courselist-info"
        sx={{ minWidth: 0, flexGrow: 1, flexBasis: 0 }}
      >
        <Grid item xs={12} sx={{ width: "100%", minWidth: 0 }}>
          <Typography
            variant="h3"
            title={title}
            className={"elipssis"}
            sx={{ maxWidth: "100%" }}
          >
            {renderTitleLinkContent(title, titleLink)}
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ width: "100%", minWidth: 0 }}>
          {content}
        </Grid>
      </Grid>
      {chips ? (
        <Grid
          container
          item
          xs="auto"
          direction="column"
          sx={{ alignItems: "end", flexShrink: 0, minWidth: 0 }}
        >
          <Grid item className="learning-courselist-item-tags">
            {chips}
          </Grid>
        </Grid>
      ) : null}
      <Grid
        item
        xs="auto"
        container
        className="learning-courselist-actions"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          flexShrink: 0,
        }}
      >
        {actions}
      </Grid>
    </Grid>
  );
};

const LearningActivityMobileListItem = ({
  title,
  type,
  chips,
  content,
  actions,
  displayImage = false,
  imageComponent,
  titleLink,
}) => {
  return (
    <Grid
      item
      container
      direction="column"
      className="learning-courselist-item-mobile"
      sx={STYLE_MOBILE_LIST_ITEM}
    >
      <Grid
        item
        sx="auto"
        container
        justifyContent="space-between"
        float="right"
      >
        {displayImage && (
          <Grid item className="learning-courselist-item-image">
            {imageComponent}
          </Grid>
        )}
        <Grid item direction="column" className="learning-courselist-item-tags">
          {chips}
        </Grid>
      </Grid>

      <Grid item>
        <Typography
          variant="h3"
          title={title}
          className={"elipssis"}
          sx={{ maxWidth: "85vw" }}
        >
          {renderTitleLinkContent(title, titleLink)}
        </Typography>
      </Grid>

      <Grid item>{content}</Grid>

      <Grid xs="auto" item container justifyContent="flex-end">
        {actions}
      </Grid>
    </Grid>
  );
};

export default LearningActivityListItem;
